/**
 * @fileoverview Sync-push boundary validation for the complex-inspections
 *               contract bundle.
 * @module import-core/contracts/complex-inspections/syncValidation
 *
 * Responsibilities:
 * - Provide `validateComplexInspectionSyncDocument(collectionName, document)`:
 *   the single gate every tenant-api and mobile-api sync push runs before
 *   `executeSyncPush`.
 * - Enforce the compatibility policy: legacy documents with no
 *   `schemaVersion` and no complex fields pass through unchanged; any
 *   `schemaVersion === 1` document, any `inspectableEntityGroups` document,
 *   and any document introducing complex facets/visit/billing fields
 *   must validate as a COMPLETE target containing document.
 *   `events[].reportArtifacts` is a worker-owned link, not a target trigger.
 *
 * Dependencies:
 * - All containing-document schemas in this contract directory.
 *
 * Architecture:
 * - Fail-closed: malformed complex/event/report data never reaches MongoDB.
 *   Legacy pass-through preserves backward-readable behavior during Phase 1A.
 *
 * Notes:
 * - `inspectableEntityGroups` sync is Phase 1B; the schema is still enforced
 *   here so a document cannot arrive through any other seam unvalidated.
 */

import { z } from 'zod';
import {
  inspectableEntityDocumentSchema,
  inspectableEntityGroupDocumentSchema,
  normalizeInspectableEntityForSync,
} from './entitySchemas';
import { canonicalContactDocumentSchema } from './contactSchemas';
import { completeViolationOnlyResponse, inspectionCaseDocumentSchema } from './caseSchemas';
import {
  reportTemplateDocumentSchema,
  validateReportTemplateDataScopeForPublish,
  generatedReportDocumentSchema,
  inspectionDeliveryRecordDocumentSchema,
  inspectionDeliveryGroupDocumentSchema,
  generatedDocumentVersionDocumentSchema,
} from './reportSchemas';
import { mobileTenantRuntimeConfigSchema } from './mobileRuntimeSchemas';

/**
 * @contract ComplexInspectionValidationError
 * Purpose: Typed boundary error so sync routes can map validation failures to
 *   conflicts/400s without inspecting zod internals.
 */
export class ComplexInspectionValidationError extends Error {
  readonly collectionName: string;
  readonly issues: z.ZodIssue[];

  constructor(collectionName: string, issues: z.ZodIssue[]) {
    super(
      `complex-inspections sync document failed validation for '${collectionName}': ` +
        issues
          .slice(0, 5)
          .map((issue) => `${issue.path.join('.') || '(root)'}: ${issue.message}`)
          .join('; '),
    );
    this.name = 'ComplexInspectionValidationError';
    this.collectionName = collectionName;
    this.issues = issues;
  }
}

type DocumentSchema = z.ZodTypeAny;

const COMPLEX_TARGET_SCHEMAS: Record<string, DocumentSchema> = {
  inspectableEntities: inspectableEntityDocumentSchema,
  inspectableEntityGroups: inspectableEntityGroupDocumentSchema,
  contacts: canonicalContactDocumentSchema,
  cases: inspectionCaseDocumentSchema,
  reportTemplates: reportTemplateDocumentSchema,
  generatedReports: generatedReportDocumentSchema,
  inspectionDeliveryRecords: inspectionDeliveryRecordDocumentSchema,
  inspectionDeliveryGroups: inspectionDeliveryGroupDocumentSchema,
  generatedDocumentVersions: generatedDocumentVersionDocumentSchema,
  // Virtual pull-only collection (document 15) — always validated so a
  // malformed runtime singleton can never reach Dexie.
  mobileTenantRuntime: mobileTenantRuntimeConfigSchema,
};

/**
 * Client-side Dexie/sync collection names → Mongo authority names. The sync
 * boundary accepts either form so tenant/mobile hosts and the local-first
 * inbound path share one alias table.
 */
const COLLECTION_ALIASES: Record<string, string> = {
  entities: 'inspectableEntities',
  inspectable_entities: 'inspectableEntities',
  inspectionCases: 'cases',
  inspection_cases: 'cases',
  generatedDocuments: 'generatedReports',
  generated_documents: 'generatedReports',
  report_templates: 'reportTemplates',
  system_report_templates: 'reportTemplates',
  mobile_tenant_runtime: 'mobileTenantRuntime',
};

/**
 * Resolve a client or Mongo collection name to the Mongo authority name used
 * by the schema map. Unknown names return unchanged (uncovered collections
 * pass through validation untouched).
 */
export function resolveComplexInspectionCollectionName(collectionName: string): string {
  return COLLECTION_ALIASES[collectionName] ?? collectionName;
}

/**
 * Collections where every document is a target document regardless of
 * `schemaVersion` (the collection exists only in the target model, or — for
 * the virtual runtime singleton — is always target-shaped).
 */
const ALWAYS_TARGET_COLLECTIONS = new Set([
  'inspectableEntityGroups',
  'mobileTenantRuntime',
]);

const COMPLEX_TOP_LEVEL_KEYS = [
  'complexAnchor',
  'complexMembership',
  'complexVisitIds',
  'complexGroupIds',
  'complexRootVisitIds',
  'reportDataScope',
  'correspondencePurpose',
  'reportSource',
] as const;

function eventHasComplexFields(event: unknown): boolean {
  if (!event || typeof event !== 'object') return false;
  const record = event as Record<string, unknown>;
  if (record.complexVisitManifest !== undefined) return true;
  if (record.complexVisitReference !== undefined) return true;
  // reportArtifacts is intentionally not a target trigger. The report worker
  // writes it onto pre-contract cases that have no schemaVersion. Treating it
  // as a complex field would force the complete case schema and reject the
  // next client push. Real visit/billing markers still require that schema.
  const billing = record.billing as Record<string, unknown> | undefined;
  if (billing && typeof billing === 'object' && billing.source !== undefined) return true;
  return false;
}

/**
 * Decide whether a pushed document must satisfy the complete target schema.
 *
 * A document is a target document when ANY of these hold:
 * - `schemaVersion === 1`
 * - the collection only exists in the target model (inspectableEntityGroups)
 * - the document introduces complex fields: `complexAnchor` /
 *   `complexMembership` at top level; `complexVisitManifest` /
 *   `complexVisitReference` / `billing.source` inside an event; or complex
 *   report/billing source fields (`reportDataScope`,
 *   `correspondencePurpose`, `reportSource`). `events[].reportArtifacts`
 *   does not reclassify a document — the report worker writes that link
 *   onto legacy cases.
 */
function requiresTargetValidation(collectionName: string, document: Record<string, unknown>): boolean {
  if (ALWAYS_TARGET_COLLECTIONS.has(collectionName)) return true;
  if (document.schemaVersion === 1) return true;
  // Local-first document templates (reportTemplates) always stamp
  // `reportDataScope: 'singleCase'` (or `'complexVisit'`) alongside builder
  // state (`_id: 'rtpl-...'`, `builderConfig`, `documentConfig`, etc.) without
  // `schemaVersion: 1`. Only explicit `schemaVersion: 1` templates run the
  // full strict target schema; non-schemaVersion-1 templates validate their
  // scope and complex-section publish topology via
  // `validateReportTemplateDataScopeForPublish` in `validateComplexInspectionSyncDocument`.
  if (collectionName === 'reportTemplates') return false;
  for (const key of COMPLEX_TOP_LEVEL_KEYS) {
    if (document[key] !== undefined) return true;
  }
  const source = document.source as Record<string, unknown> | undefined;
  if (source && typeof source === 'object' && source.reportDataScope !== undefined) return true;
  if (Array.isArray(document.events) && document.events.some(eventHasComplexFields)) return true;
  return false;
}

/**
 * @security validateComplexInspectionSyncDocument
 * Purpose: Boundary gate run inside tenant-api and mobile-api sync push
 *   immediately before `executeSyncPush`.
 * Validation: For covered collections, target documents are parsed against
 *   the COMPLETE strict containing-document schema (not fragments); the
 *   parsed document is returned. Documents that do not meet the target
 *   criteria — including all documents in uncovered collections — pass
 *   through unchanged so legacy mutation behavior is preserved.
 * Trust Boundary: Anything flagged as target that fails validation throws
 *   `ComplexInspectionValidationError`; the write must be rejected.
 */
export function validateComplexInspectionSyncDocument<T = unknown>(
  collectionName: string,
  document: T,
): T {
  const resolvedName = resolveComplexInspectionCollectionName(collectionName);
  const schema = COMPLEX_TARGET_SCHEMAS[resolvedName];
  if (!schema) return document;
  if (!document || typeof document !== 'object' || Array.isArray(document)) {
    throw new ComplexInspectionValidationError(collectionName, [
      { code: 'custom', path: [], message: 'sync document must be an object' } as z.ZodIssue,
    ]);
  }
  const record = document as Record<string, unknown>;
  if (!requiresTargetValidation(resolvedName, record)) {
    if (resolvedName === 'reportTemplates') {
      const scopeValidation = validateReportTemplateDataScopeForPublish(record);
      if (!scopeValidation.valid) {
        throw new ComplexInspectionValidationError(
          collectionName,
          scopeValidation.issues.map((issue) => ({
            code: z.ZodIssueCode.custom,
            path: issue.path,
            message: issue.message,
          })),
        );
      }
    }
    return document;
  }
  let candidate: unknown = document;
  if (resolvedName === 'cases') {
    // A nested violation write can create a response containing only
    // `violation` when its parent response was never migrated. Complete that
    // stub before strict parsing so the violation remains readable.
    const responses = record.responses;
    if (responses && typeof responses === 'object' && !Array.isArray(responses)) {
      const completed: Record<string, unknown> = {};
      let changed = false;
      for (const [responseKey, response] of Object.entries(responses as Record<string, unknown>)) {
        const repaired = completeViolationOnlyResponse(responseKey, response);
        completed[responseKey] = repaired;
        changed ||= repaired !== response;
      }
      if (changed) candidate = { ...record, responses: completed };
    }
  }
  if (resolvedName === 'inspectableEntities') {
    // Heal BSON-null optionals and legacy contact links first, exactly as the
    // complex-mutation write path does, so pull and push agree on what a
    // canonical entity is. Unrepairable contact data surfaces as an issue.
    try {
      candidate = normalizeInspectableEntityForSync(record);
    } catch (err) {
      if (err instanceof z.ZodError) {
        throw new ComplexInspectionValidationError(collectionName, err.issues);
      }
      throw err;
    }
  }
  const result = schema.safeParse(candidate);
  if (!result.success) {
    throw new ComplexInspectionValidationError(collectionName, result.error.issues);
  }
  return result.data as T;
}

// ---------------------------------------------------------------------------
// Protected server-authoritative fields (change-level gate)
// ---------------------------------------------------------------------------

const OBJECT_ID_HEX_SYNC = /^[a-f\d]{24}$/i;

/**
 * Normalize a persisted value for protected-field comparison: BSON
 * `ObjectId`/ObjectId-like → hex string, `Date` → ISO string, objects sorted
 * by key. Undefined entries are dropped so absent-vs-undefined never differs.
 */
function normalizeForCompare(value: unknown): unknown {
  if (value === undefined) return undefined;
  if (value === null) return null;
  if (value instanceof Date) return value.toISOString();
  if (Array.isArray(value)) return value.map(normalizeForCompare);
  if (typeof value === 'object') {
    const record = value as Record<string, unknown>;
    if (
      typeof record.toHexString === 'function' &&
      OBJECT_ID_HEX_SYNC.test((record as { toHexString: () => string }).toHexString())
    ) {
      return (record as { toHexString: () => string }).toHexString();
    }
    const out: Record<string, unknown> = {};
    for (const key of Object.keys(record).sort()) {
      const normalized = normalizeForCompare(record[key]);
      if (normalized !== undefined) out[key] = normalized;
    }
    return out;
  }
  return value;
}

function stableStringify(value: unknown): string {
  return JSON.stringify(normalizeForCompare(value));
}

type ProtectedProjection = Record<string, unknown>;

/**
 * Extract the server-authoritative protected projection per collection.
 * Returned value is compared between `assumedMasterState` and
 * `newDocumentState`; `null`/empty means the document carries no protected
 * state (legacy or ordinary writes stay freely mutable).
 */
const PROTECTED_PROJECTIONS: Record<
  string,
  (doc: Record<string, unknown>) => ProtectedProjection
> = {
  inspectableEntities: (doc) => {
    // Complex facets + the complex-origin topology inside contact links.
    // Direct-origin edits remain allowed — only `originType:'complex'`
    // origins are protected.
    const contacts = Array.isArray(doc.contacts) ? doc.contacts : [];
    const complexOriginsByContact = contacts
      .map((link: any) => ({
        contactId: String(link?.contactId ?? ''),
        complexOrigins: (Array.isArray(link?.linkOrigins) ? link.linkOrigins : []).filter(
          (origin: any) => origin?.originType === 'complex',
        ),
      }))
      .filter((entry) => entry.complexOrigins.length > 0)
      .sort((a, b) => a.contactId.localeCompare(b.contactId));
    return {
      complexAnchor: doc.complexAnchor,
      complexMembership: doc.complexMembership,
      complexOriginsByContact,
    };
  },
  cases: (doc) => {
    const events = Array.isArray(doc.events) ? doc.events : [];
    return {
      complexVisitIds: doc.complexVisitIds,
      complexGroupIds: doc.complexGroupIds,
      complexRootVisitIds: doc.complexRootVisitIds,
      events: events
        .map((event: any) => ({
          eventIndex: event?.eventIndex,
          complexVisitManifest: event?.complexVisitManifest,
          complexVisitReference: event?.complexVisitReference,
          reportArtifacts: event?.reportArtifacts,
          billingSource: event?.billing?.source,
        }))
        .filter(
          (entry) =>
            entry.complexVisitManifest !== undefined ||
            entry.complexVisitReference !== undefined ||
            entry.reportArtifacts !== undefined ||
            entry.billingSource !== undefined,
        ),
    };
  },
  generatedReports: (doc) => ({
    source: doc.source,
    reportDataScope: doc.reportDataScope,
    correspondencePurpose: doc.correspondencePurpose,
    visitId: doc.visitId,
    groupId: doc.groupId,
    generationIdentity: doc.generationIdentity,
  }),
  inspectionDeliveryRecords: (doc) => {
    // Only a COMPLEX report's delivery record is server-authoritative. A
    // single-inspection report (including a one-off inspection of a building
    // that happens to sit inside a complex, e.g. a summer pool inspection)
    // has an ordinary delivery record that clients create and sync; protecting
    // its identifiers would reject that normal flow. The scope is therefore
    // read from the record's own report source, not from complex membership.
    const scope = (doc.reportSource as { reportDataScope?: unknown } | undefined)?.reportDataScope;
    if (scope !== 'complexVisit') return {};
    return {
      reportSource: doc.reportSource,
      generatedDocumentId: doc.generatedDocumentId,
      reportVersionId: doc.reportVersionId,
      reportChecksum: doc.reportChecksum,
    };
  },
  generatedDocumentVersions: (doc) => ({
    reportSource: doc.reportSource,
    recordId: doc.recordId,
    generatedDocumentId: doc.generatedDocumentId,
    versionNumber: doc.versionNumber,
    reportChecksum: doc.reportChecksum,
  }),
};

function projectionIsEmpty(projection: ProtectedProjection): boolean {
  return Object.values(projection).every(
    (value) => value === undefined || (Array.isArray(value) && value.length === 0),
  );
}

function protectedFingerprint(
  collectionName: string,
  doc: Record<string, unknown> | null | undefined,
): string | null {
  const extractor = PROTECTED_PROJECTIONS[collectionName];
  if (!extractor || !doc || typeof doc !== 'object') return null;
  const projection = extractor(doc);
  if (projectionIsEmpty(projection)) return null;
  return stableStringify(projection);
}

/**
 * @contract isComplexInspectionProtectedCollection
 * Purpose: Identify collection sync targets with server-authoritative
 *   complex-inspection fields.
 * Fields: collectionName — client or Mongo collection name.
 * Rules: aliases resolve through the shared collection-name resolver.
 * Sync: used by API write-time guards.
 */
export function isComplexInspectionProtectedCollection(collectionName: string): boolean {
  return PROTECTED_PROJECTIONS[
    resolveComplexInspectionCollectionName(collectionName)
  ] !== undefined;
}

/**
 * @security assertComplexInspectionProtectedFieldsUnchanged
 * Validation: Compare the protected projection of the persisted document
 *   against the effective document after applying only the incoming top-level
 *   `$set` fields.
 * Trust Boundary: Uses the server's persisted document, never
 *   client-supplied `assumedMasterState`.
 */
export function assertComplexInspectionProtectedFieldsUnchanged(
  collectionName: string,
  persistedDocument: unknown,
  newDocumentState: unknown,
): void {
  const resolvedName = resolveComplexInspectionCollectionName(collectionName);
  if (!PROTECTED_PROJECTIONS[resolvedName]) return;
  if (
    newDocumentState === null ||
    typeof newDocumentState !== 'object' ||
    Array.isArray(newDocumentState) ||
    ![Object.prototype, null].includes(Object.getPrototypeOf(newDocumentState))
  ) {
    return;
  }

  const newRecord = newDocumentState as Record<string, unknown>;
  const after = protectedFingerprint(resolvedName, newRecord);
  if (persistedDocument === null || persistedDocument === undefined) {
    if (after === null) return;
    throw new ComplexInspectionValidationError(collectionName, [
      {
        code: 'custom',
        path: [],
        message:
          'protected complex-inspection fields are server-authoritative and cannot be introduced through collection sync',
      } as z.ZodIssue,
    ]);
  }

  const effective: Record<string, unknown> = {
    ...(typeof persistedDocument === 'object' && persistedDocument !== null
      ? persistedDocument as Record<string, unknown>
      : {}),
  };
  for (const key of Object.keys(newRecord)) {
    if (newRecord[key] !== undefined) effective[key] = newRecord[key];
  }

  const before = protectedFingerprint(
    resolvedName,
    persistedDocument as Record<string, unknown>,
  );
  if (before === protectedFingerprint(resolvedName, effective)) return;
  throw new ComplexInspectionValidationError(collectionName, [
    {
      code: 'custom',
      path: [],
      message:
        'protected complex-inspection fields are server-authoritative and cannot be mutated through collection sync',
    } as z.ZodIssue,
  ]);
}

/**
 * @security validateComplexInspectionSyncChange
 * Purpose: Change-level gate for sync push. Runs the complete target
 *   document validation when required (delegating to
 *   `validateComplexInspectionSyncDocument`), then rejects client mutation
 *   of protected relationship/server-authoritative fields by comparing
 *   `assumedMasterState` against `newDocumentState` as a cheap early check.
 *   The authoritative protected-field check compares against the persisted
 *   document at write time.
 * Validation:
 * - Creation carrying any protected field is rejected — Phase 1B
 *   operation/server worker boundaries own them.
 * - An unchanged protected projection on an ordinary full-document edit is
 *   allowed; any change/addition/removal is rejected.
 * - Tombstones (`_deleted:true`) are NOT skipped: a legacy partial tombstone
 *   with no target/protected fields passes, while a tombstone that claims
 *   target identity must carry a valid `_id` and keep protected fields
 *   unchanged.
 * Trust Boundary: protected fields are server-authoritative; the write-time
 *   persisted-document check is the authoritative mutation boundary.
 */
export function validateComplexInspectionSyncChange(
  collectionName: string,
  assumedMasterState: unknown,
  newDocumentState: unknown,
): void {
  const resolvedName = resolveComplexInspectionCollectionName(collectionName);
  const schema = COMPLEX_TARGET_SCHEMAS[resolvedName];
  if (!schema) return;
  if (newDocumentState === undefined || newDocumentState === null) return;
  if (typeof newDocumentState !== 'object' || Array.isArray(newDocumentState)) {
    throw new ComplexInspectionValidationError(collectionName, [
      { code: 'custom', path: [], message: 'sync change document must be an object' } as z.ZodIssue,
    ]);
  }
  const record = newDocumentState as Record<string, unknown>;
  const isTombstone = record._deleted === true;

  if (!isTombstone) {
    validateComplexInspectionSyncDocument(collectionName, record);
  } else if (requiresTargetValidation(resolvedName, record)) {
    // A tombstone is a deletion marker, not a complete document — it cannot
    // satisfy the full schema, but a target tombstone must still be
    // well-formed and leave protected state untouched.
    const id = record._id;
    const idValid =
      (typeof id === 'string' && OBJECT_ID_HEX_SYNC.test(id)) ||
      (typeof id === 'object' &&
        id !== null &&
        typeof (id as { toHexString?: unknown }).toHexString === 'function' &&
        OBJECT_ID_HEX_SYNC.test((id as { toHexString: () => string }).toHexString()));
    if (!idValid) {
      throw new ComplexInspectionValidationError(collectionName, [
        {
          code: 'custom',
          path: ['_id'],
          message: 'target tombstone requires a valid _id',
        } as z.ZodIssue,
      ]);
    }
  }

  const before = protectedFingerprint(
    resolvedName,
    assumedMasterState as Record<string, unknown> | null | undefined,
  );
  const after = protectedFingerprint(resolvedName, record);
  if (after === before) return;
  throw new ComplexInspectionValidationError(collectionName, [
    {
      code: 'custom',
      path: [],
      message:
        before === null
          ? 'protected complex-inspection fields are server-authoritative and cannot be introduced through collection sync'
          : 'protected complex-inspection fields are server-authoritative and cannot be mutated through collection sync',
    } as z.ZodIssue,
  ]);
}
